import { useCallback, useRef, useState } from "react";
import { apiClient } from "../lib/api";
import type { Celebrity } from "../lib/types";

interface UseCelebritySearch {
  results: Celebrity[];
  isLoading: boolean;
  error: string | null;
  search: (query: string) => void;
}

export function useCelebritySearch(): UseCelebritySearch {
  const [results, setResults] = useState<Celebrity[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const search = useCallback((query: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(async () => {
      if (!query.trim()) {
        setResults([]);
        return;
      }
      setIsLoading(true);
      setError(null);
      try {
        const data = await apiClient.get<Celebrity[]>(
          `/api/celebrities/search?q=${encodeURIComponent(query)}`,
        );
        setResults(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Search failed");
      } finally {
        setIsLoading(false);
      }
    }, 300);
  }, []);

  return { results, isLoading, error, search };
}
